import usePageError from "../connectors/nav/usePageError";
import RedirectToBookshelf from "../../../../common/ui/RedirectToBookshelf";
import React from "react";
import useISBN from "../connectors/viewer/useISBN";
import { Button } from "@vitalsource/vstui";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import { PageError } from "../api/features/nav/load-status/types";
import useRebuildProgress from "../connectors/book-rebuild/useRebuildProgress";
import { CheckmarkCircleFill } from "@vitalsource/vstui-icons";
import { cloud, green } from "@vitalsource/vstui/colors";
import ProgressCircle from "../../ui/default/common/ProgressCircle";
import useLibraryConfig from "../../ui/default/features/library/useLibraryConfig";
import PageErrorBrowserNotSupported from "./PageErrorBrowserNotSupported";
import useBookInfo from "../connectors/bookinfo/useBookInfo";
import ReaderDialog, {
  ReaderDialogType,
} from "../../ui/default/common/ReaderDialog";
import { useParams } from "react-router-dom";

const COVER_SIZE = 120;

const Styled = {
  ErrorContainer: styled.div`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    position: relative;
    padding-top: 8px;
  `,
  CoverImage: styled.img`
    width: 96px;
    margin-right: 14px;
    border-radius: 2px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
  `,
  BodyContent: styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding-top: 2px;
  `,
  Detail: styled.div`
    padding-top: 16px;
  `,
};

const RebuildStyled = {
  RebuildingMessage: styled.div`
    padding-bottom: 19px;
  `,
  Updating: styled.div`
    color: ${cloud.getShade(6)};
    position: relative;
    padding-left: 46px;
    padding-top: 7px;
    font-weight: 500;

    svg {
      position: absolute;
      top: 0;
      left: 0;
    }
  `,
  SuccessMessage: styled.p`
    position: relative;
    padding-bottom: 8px;
    padding-left: 27px;

    svg {
      position: absolute;
      top: 1px;
      left: 0;
    }
  `,
  BottomButton: styled(Button)`
    align-self: flex-end;
    margin-top: auto;
  `,
};

function LocalError({ children }) {
  const intl = useIntl();
  const isbn = useISBN();

  function getCoverUrl(size) {
    return `https://covers.vitalbook.com/vbid/${isbn}/width/${size}`;
  }

  return (
    <ReaderDialog
      type={ReaderDialogType.Vstui}
      open
      alert
      requireInteraction
      title={intl.formatMessage({ id: "errors.bookErrorTitle" })}
      size="small"
    >
      <Styled.ErrorContainer>
        <Styled.CoverImage
          src={getCoverUrl(COVER_SIZE)}
          srcSet={`${getCoverUrl(COVER_SIZE * 2)} 2x`}
          alt=""
        />
        <Styled.BodyContent>{children}</Styled.BodyContent>
      </Styled.ErrorContainer>
    </ReaderDialog>
  );
}

function RebuildingError() {
  const percent = useRebuildProgress();

  if (percent === 100) {
    return (
      <>
        <RebuildStyled.SuccessMessage>
          <CheckmarkCircleFill color={green.getShade(6)} />
          <FormattedMessage id="errors.bookRebuilding.bookUpdated" />
        </RebuildStyled.SuccessMessage>
        <RebuildStyled.BottomButton onClick={() => window.location.reload()}>
          <FormattedMessage id="base.reload" />
        </RebuildStyled.BottomButton>
      </>
    );
  }

  return (
    <>
      <RebuildStyled.RebuildingMessage>
        <FormattedMessage id="errors.bookRebuilding" />
      </RebuildStyled.RebuildingMessage>
      <RebuildStyled.Updating>
        <ProgressCircle percent={percent} />
        <FormattedMessage id="base.updating" />
      </RebuildStyled.Updating>
    </>
  );
}

export default function PageErrors() {
  let error = usePageError();
  const isbn = useISBN();
  const { isLibraryEnabled, libraryHref } = useLibraryConfig();
  const { statusCode: bookInfoStatusCode } = useBookInfo();
  const params = useParams<{ vbid: string; location?: string }>();
  if (bookInfoStatusCode === 444) {
    // api calls can return plain text "browser not supported"
    // loading that into a content doc iframe causes an undetectable error
    // so use this as a sign things are just not going to work out
    error = PageError.BROWSER_NOT_SUPPORTED;
  }

  switch (error) {
    case PageError.LOGGED_OUT:
      // Covered by general logged out case. Don't show anything special.
      return null;
    case PageError.BOOK_NOT_ACTIVATED:
      return (
        <RedirectToBookshelf
          path={
            `/activate/${isbn}` + (params.location ? `/${params.location}` : "")
          }
        />
      );
    case PageError.ACCESS_DENIED:
      return (
        <LocalError>
          <FormattedMessage id="errors.bookAccessDenied" />
          {isLibraryEnabled && (
            <Styled.Detail>
              <Button href={libraryHref} inline>
                <FormattedMessage id="base.backToLibrary" />
              </Button>
            </Styled.Detail>
          )}
        </LocalError>
      );
    case PageError.BOOK_NOT_FOUND:
      return (
        <LocalError>
          <FormattedMessage id="errors.bookDoesntExist" />
        </LocalError>
      );
    case PageError.BOOK_REBUILDING:
      return (
        <LocalError>
          <RebuildingError />
        </LocalError>
      );
    case PageError.PAGE_NOT_FOUND:
      // Handled in middleware
      // @see auto-run.ts
      return null;
    case PageError.BOT_DETECTED:
      // Page should be presented to the user as normal, no special handling
      return null;
    case PageError.BROWSER_NOT_SUPPORTED:
      return <PageErrorBrowserNotSupported />;
    case PageError.UNKNOWN:
      return <RedirectToBookshelf path="" />;
    default:
      return null;
  }
}
